/**
 * MAC address formats used by ScanReach hardware.
 *
 * Wearables (SRT-PT) carry a 6-byte BLE MAC; nodes (SRT-MN) report an 8-byte EUI-64.
 * The Admin tool is the only place these are typed by hand, and the backend stores
 * whatever it is given — a wearable was once registered as "d8:b15f:0a:25:d1"
 * (a colon short of a MAC), which then had to be corrected directly in the database.
 * Validate before insert so a typo is rejected in the dialog instead.
 */

/** Six colon-separated hex byte pairs, e.g. d8:b1:5f:0a:25:d1 */
const BLE_MAC_PATTERN = /^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i;

/** Eight colon-separated hex byte pairs, e.g. 00:12:4b:00:1e:1d:de:51 */
const EUI64_MAC_PATTERN = /^([0-9a-f]{2}:){7}[0-9a-f]{2}$/i;

/** Device types whose MAC is an 8-byte EUI-64 rather than a 6-byte BLE MAC. */
const EUI64_DEVICE_TYPES = ['SRT-MN'];

export function isBleMacAddress(mac: string): boolean {
  return BLE_MAC_PATTERN.test((mac ?? '').trim());
}

export function isEui64MacAddress(mac: string): boolean {
  return EUI64_MAC_PATTERN.test((mac ?? '').trim());
}

/**
 * Validates a MAC address against the format expected for the given device type.
 * Unknown device types fall back to accepting either length, so adding a new type
 * to the backend does not silently block the Admin tool.
 */
export function isValidMacAddressForDeviceType(mac: string, deviceType: string | null | undefined): boolean {
  if (deviceType === 'SRT-PT') return isBleMacAddress(mac);
  if (EUI64_DEVICE_TYPES.includes(deviceType)) return isEui64MacAddress(mac);
  return isBleMacAddress(mac) || isEui64MacAddress(mac);
}

export function macAddressFormatHint(deviceType: string | null | undefined): string {
  if (deviceType === 'SRT-PT') return 'xx:xx:xx:xx:xx:xx (6 hex pairs, e.g. d8:b1:5f:0a:25:d1)';
  if (EUI64_DEVICE_TYPES.includes(deviceType))
    return 'xx:xx:xx:xx:xx:xx:xx:xx (8 hex pairs, e.g. 00:12:4b:00:1e:1d:de:51)';
  return 'colon separated hex pairs, 6 or 8 bytes';
}
